import React from 'react'
import type { AppLayoutMode } from '../../contexts/appViewportContext'
import {
  ONLINE_PVP_ASSET_BASE_URL,
  localPublicAssetUrl,
  pvpDirectChallengeAssetUrl,
  pvpHubAssetUrl,
  updatePvpAssetUrl,
} from './utils/pvpAssetUrls'

/** Vercel Blob root for PvP UI art (lobby, battle, tournament). */
export const PVP_ASSET_BASE_URL = ONLINE_PVP_ASSET_BASE_URL
const BACKGROUND_URL = updatePvpAssetUrl('battle-bg.webp')

/** Main `/pvp` hub lobby — source toggled in `pvpAssetUrls.ts` (`PVP_HUB_ASSET_SOURCE`). */
export const pvpLobbyHubBgUrl = pvpHubAssetUrl('bg.webp')
export const pvpLobbyTitleDividerLeftUrl = pvpHubAssetUrl('title-divider-left.webp')
export const pvpLobbyTitleDividerRightUrl = pvpHubAssetUrl('title-divider-right.webp')

export function pvpLobbyHubAssetUrl(fileName: string): string {
  return pvpHubAssetUrl(fileName)
}

/** Direct challenge send / join CTA — Vercel Blob `pvp/direct-challenge/sendChallengeButtonBg.webp`. */
export const pvpDirectChallengeSendButtonBgUrl = pvpDirectChallengeAssetUrl('sendChallengeButtonBg.webp')

/** Ranked / MMR hint icon — Blob `pvp/direct-challenge/rankedMMR.webp`. */
export const pvpDirectChallengeRankedMmrIconUrl = pvpDirectChallengeAssetUrl('rankedMMR.webp')

const BATTLE_BACKGROUND_URL = BACKGROUND_URL
const BATTLE_ARENA_URL =
  `${PVP_ASSET_BASE_URL}/battle-arena.webp`

/** Fallback for VS / waiting UI skewed slots (not join-tile pairs). */
const PVP_BATTLE_CARD_TILE_BG_URL = `${PVP_ASSET_BASE_URL}/char-2-5.webp`

const PVP_1CHAR_BATTLE_CARD_DIR = `${PVP_ASSET_BASE_URL}/1v1`
const PVP_2CHAR_BATTLE_CARD_DIR = `${PVP_ASSET_BASE_URL}/2v2`
const PVP_3CHAR_BATTLE_CARD_DIR = `${PVP_ASSET_BASE_URL}/3v3`

const PVP_MODE_BADGE_DIR = PVP_ASSET_BASE_URL

/**
 * Live battle feed art (`public/assets/images/land/PvP/live-battle-feed/`).
 * `arrow-button.webp` only; title + body use standard panel chrome in `PvPLobbyLiveBattleFeed`.
 */
const PVP_LIVE_BATTLE_FEED_DIR = `${PVP_MODE_BADGE_DIR}/live-battle-feed`
export const pvpLiveBattleFeedArrowUrl = `${PVP_LIVE_BATTLE_FEED_DIR}/arrow-button.webp`

/** Win / lose reward strip icons (`land/PvP/`). */
export const pvpWinnerIconUrl = `${PVP_MODE_BADGE_DIR}/winner.webp`
export const pvpLoserIconUrl = `${PVP_MODE_BADGE_DIR}/loser.webp`

/** Waiting room — stop matchmaking (Vercel Blob `pvp/stop-matchfinding-button-bg.webp`). */
export const PVP_STOP_MATCHFINDING_BUTTON_BG_URL = `${PVP_ASSET_BASE_URL}/stop-matchfinding-button-bg.webp`

/** Join battle card: bottom/side fade overlay (static; sits above zooming slice art, inside card clip). */
export const pvpJoinCardFadeOverlayUrl = `${PVP_MODE_BADGE_DIR}/fading-inside.webp`

/**
 * Tournament hub art (`pvp/tournament/` on Vercel Blob). Land page entry icon stays on {@link PVP_MODE_BADGE_DIR}.
 */
const PVP_TOURNAMENT_ASSET_BASE_URL =
  'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/pvp/tournament'

/** Join tournament CTA */
export const pvpTournamentJoinButtonUrl = `${PVP_TOURNAMENT_ASSET_BASE_URL}/join-button-bg.webp`
/** Enter battle CTA */
export const pvpTournamentEnterBattleButtonUrl = `${PVP_TOURNAMENT_ASSET_BASE_URL}/enter-battle-button-bg.webp`

/** First-time joiner one-time ECS reward (enter-battle hub) — Vercel Blob `pvp/tournament/`. */
export const pvpTournamentOneTimeRewardPanelBgUrl = `${PVP_TOURNAMENT_ASSET_BASE_URL}/one-time-reward-bg.webp`
export const pvpTournamentOneTimeClaimButtonBgUrl = `${PVP_TOURNAMENT_ASSET_BASE_URL}/one-time-claim-button-bg.webp`

/** Land hub tournament entry icon (`pvp/tournament-button.webp` on main `pvp` blob — not under `pvp/tournament/`). */
export const pvpTournamentLandButtonUrl = `${PVP_MODE_BADGE_DIR}/tournament-button.webp`

/**
 * Lobby / character-select / waiting shells: base background only.
 * Additional arena/fade layers are rendered via `PvPShellBackgroundLayers` so the arena can stay semi-transparent
 * while the dark fade overlay remains fully opaque.
 */
export const pvpShellRootBackgroundStyle: React.CSSProperties = {
  backgroundImage: `url(${BATTLE_BACKGROUND_URL})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
}

/** Main PvP lobby (`/pvp`): same base background treatment as the shell pages. */
export const pvpLobbyBareBackgroundStyle: React.CSSProperties = {
  backgroundImage: `url(${BATTLE_BACKGROUND_URL})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
}

/** Tournament join hub (`/tournament`) backdrop */
export const PVP_TOURNAMENT_JOIN_PAGE_BG_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/pvp-tournament-bg.webp`

/** Join hub (`/tournament`) header title artwork */
export const PVP_TOURNAMENT_JOIN_PAGE_TITLE_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/title.webp`

/** Join hub — total joiners plaque when no event */
export const PVP_TOURNAMENT_TOTAL_JOINERS_BG_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/total-joiner.webp`

/** Join hub — “How it works” infographic when no event */
export const PVP_TOURNAMENT_HOW_IT_WORKS_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/how-it-works.webp`

/** Join hub — season banner */
export const PVP_TOURNAMENT_SEASON_FLAG_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/season-flag.webp`

/** Join hub — countdown strip */
export const PVP_TOURNAMENT_TIME_PANEL_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/time-panel.webp`

/** Enter battle hub — overview panel */
export const PVP_TOURNAMENT_OVERVIEW_WEBP_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/overview.webp`

/** Enter battle hub — weekly stats panel infographic */
export const PVP_TOURNAMENT_WEEKLY_STATS_WEBP_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/weekly-stats.webp`

/** Floating round nav — `/tournament/battle` leaderboard */
export const PVP_TOURNAMENT_LEADERBOARD_ROUND_BUTTON_BG_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/leaderboard-button-bg.webp`
/** Floating round nav — rewards */
export const PVP_TOURNAMENT_REWARDS_ROUND_BUTTON_BG_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/rewards-button-bg.webp`
/** Floating round nav — activity */
export const PVP_TOURNAMENT_ACTIVITY_ROUND_BUTTON_BG_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/activity-button-bg.webp`

/** Enter battle — leaderboard modal title strip */
export const PVP_TOURNAMENT_LEADERBOARD_MODAL_TITLE_BG_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/leaderboard-title-bg.webp`

/** Enter battle — activity modal title strip */
export const PVP_TOURNAMENT_ACTIVITY_MODAL_TITLE_BG_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/activity-title-bg.webp`
/** Enter battle — rewards modal title strip */
export const PVP_TOURNAMENT_REWARD_MODAL_TITLE_BG_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/reward-title-bg.webp`
/** Enter battle — leaderboard modal list panel */
export const PVP_TOURNAMENT_LEADERBOARD_MODAL_BODY_BG_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/leaderboard-body-bg.webp`
/** Enter battle — leaderboard / rewards / activity modals close control — Vercel Blob `pvp/tournament/`. */
export const PVP_TOURNAMENT_MODAL_CLOSE_BUTTON_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/close-button.webp`
/** Strip parchment shell styling so the infographic is not framed twice (used when `!tournament`). */
export const tournamentHubJoinEmptyMainShellStyle: React.CSSProperties = {
  border: 'none',
  outline: 'none',
  backgroundImage: 'none',
  backgroundColor: 'transparent',
  boxShadow: 'none',
}

/** Hosted arena art — `/tournament/battle` hub (same file as join page BG; different layout treatment). */
export const PVP_TOURNAMENT_LOBBY_BG_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/pvp-tournament-bg.webp`

/** Full-page join / enter-battle backdrop — arena art with a light dark fade over it (`pvp-tournament-bg.webp`). */
export const pvpTournamentJoinPageBackgroundStyle: React.CSSProperties = {
  backgroundColor: '#1d1a17',
  backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.1) 50%, rgba(0, 0, 0, 0.14) 100%), url(${PVP_TOURNAMENT_JOIN_PAGE_BG_URL})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center top',
  backgroundRepeat: 'no-repeat',
}

/** Battle hub (`/tournament/battle`): single hosted backdrop only. */
export const pvpTournamentLobbyBackgroundStyle: React.CSSProperties = {
  backgroundColor: '#151210',
  backgroundImage: `url(${PVP_TOURNAMENT_LOBBY_BG_URL})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
}

/** Rank / score / streak row. */
export const PVP_TOURNAMENT_RANK_BG_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/rank-bg.webp`

/** Main hub shell behind tabs + body. */
export const PVP_TOURNAMENT_BODY_BG_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/tournament-body-bg.webp`

/** PvP lobby (`/pvp`) banner for tournament joiners. */
export const PVP_TOURNAMENT_LOBBY_BANNER_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/tournament-banner.webp`

/** Ranked lobby tournament icon (`public/assets/tournament-banner.webp`). */
export const PVP_TOURNAMENT_LOBBY_BANNER_ICON_URL = localPublicAssetUrl('/assets/tournament-banner.webp')

/** Gold / silver / bronze podium shields — rewards tab (`TournamentRewardsTab`) + tournament leaderboard rank column. */
export const PVP_TOURNAMENT_RANK_REWARD_ICON_URLS = [
  `${PVP_TOURNAMENT_ASSET_BASE_URL}/rank-1-icon.webp`,
  `${PVP_TOURNAMENT_ASSET_BASE_URL}/rank-2-icon.webp`,
  `${PVP_TOURNAMENT_ASSET_BASE_URL}/rank-3-icon.webp`,
] as const

export function pvpTournamentRankRewardIconUrl(place: 1 | 2 | 3): string {
  return PVP_TOURNAMENT_RANK_REWARD_ICON_URLS[place - 1]
}

/**
 * Enter-battle “Tournament rank rewards” modal — podium + rank bands.
 * Same hosted base as {@link PVP_TOURNAMENT_ASSET_BASE_URL} (`pvp/tournament/` on Vercel Blob).
 */
export const PVP_TOURNAMENT_REWARD_PODIUM_RANK_1_BG_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/rank-1-bg.webp`
export const PVP_TOURNAMENT_REWARD_PODIUM_RANK_1_ECS_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/rank-1-ecs-bg.webp`
export const PVP_TOURNAMENT_REWARD_PODIUM_RANK_2_BG_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/rank-2-bg.webp`
export const PVP_TOURNAMENT_REWARD_PODIUM_RANK_2_ECS_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/rank-2-ecs-bg.webp`
export const PVP_TOURNAMENT_REWARD_PODIUM_RANK_3_BG_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/rank-3-bg.webp`
export const PVP_TOURNAMENT_REWARD_PODIUM_RANK_3_ECS_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/rank-3-ecs-bg.webp`
export const PVP_TOURNAMENT_REWARD_RANK_4_10_CARD_BG_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/rank-4-10-bg.webp`
export const PVP_TOURNAMENT_REWARD_RANK_BIG_TITLE_BG_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/rank-big-title-bg.webp`
export const PVP_TOURNAMENT_REWARD_RANK_RANGE_TITLE_BG_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/rank-range-title-bg.webp`
export const PVP_TOURNAMENT_REWARD_RANK_SMALL_TITLE_BG_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/rank-small-title-bg.webp`
/** `TournamentResultYourPerformanceRow` — rank number sits on this art (no stat-card chrome). */
export const PVP_TOURNAMENT_MY_RANK_NUMBER_BG_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/my-rank-number-bg.webp`

/** Join page — `TournamentResultModal` title strip (`result-title-bg.webp`). */
export const PVP_TOURNAMENT_RESULT_MODAL_TITLE_BG_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/result-title-bg.webp`

/** PvP lobby (`/pvp`) champion strip after the weekly event ends (`tournament-champion-banner-bg.webp`, VP8X 800×347). */
export const PVP_TOURNAMENT_LOBBY_CHAMPION_BANNER_BG_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/tournament-champion-banner-bg.webp`
/** Matches {@link PVP_TOURNAMENT_LOBBY_CHAMPION_BANNER_BG_URL} intrinsic aspect (width / height). */
export const PVP_TOURNAMENT_LOBBY_CHAMPION_BANNER_ASPECT_RATIO = 800 / 347

/** Champion strip title plate (`tournament-champion-banner-title-bg.webp`, VP8X 700×161). */
export const PVP_TOURNAMENT_LOBBY_CHAMPION_BANNER_TITLE_BG_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/tournament-champion-banner-title-bg.webp`
/** Width / height of {@link PVP_TOURNAMENT_LOBBY_CHAMPION_BANNER_TITLE_BG_URL}. */
export const PVP_TOURNAMENT_LOBBY_CHAMPION_BANNER_TITLE_ASPECT_RATIO = 700 / 161

/** Champion strip (`/pvp`) — timer chrome at bottom (`tournament-champion-banner-timer-bg.webp`). */
export const PVP_TOURNAMENT_LOBBY_CHAMPION_BANNER_TIMER_BG_URL = `${PVP_TOURNAMENT_ASSET_BASE_URL}/tournament-champion-banner-timer-bg.webp`

/** `TournamentResultTop3ChampionsBody` — ornate frame + rank badge; avatar is centered in the clear circle. */
export const PVP_TOURNAMENT_RESULT_AVATAR_CONTAINER_URLS: readonly [string, string, string] = [
  `${PVP_TOURNAMENT_ASSET_BASE_URL}/rank-1-avatar-container.webp`,
  `${PVP_TOURNAMENT_ASSET_BASE_URL}/rank-2-avatar-container.webp`,
  `${PVP_TOURNAMENT_ASSET_BASE_URL}/rank-3-avatar-container.webp`,
] as const

export function pvpTournamentResultAvatarContainerUrl(place: 1 | 2 | 3): string {
  return PVP_TOURNAMENT_RESULT_AVATAR_CONTAINER_URLS[place - 1]
}

export const PVP_TOURNAMENT_REWARD_MODAL_BODY_PRELOAD_URLS: readonly string[] = [
  PVP_TOURNAMENT_REWARD_PODIUM_RANK_1_BG_URL,
  PVP_TOURNAMENT_REWARD_PODIUM_RANK_1_ECS_URL,
  PVP_TOURNAMENT_REWARD_PODIUM_RANK_2_BG_URL,
  PVP_TOURNAMENT_REWARD_PODIUM_RANK_2_ECS_URL,
  PVP_TOURNAMENT_REWARD_PODIUM_RANK_3_BG_URL,
  PVP_TOURNAMENT_REWARD_PODIUM_RANK_3_ECS_URL,
  PVP_TOURNAMENT_REWARD_RANK_4_10_CARD_BG_URL,
  PVP_TOURNAMENT_REWARD_RANK_BIG_TITLE_BG_URL,
  PVP_TOURNAMENT_REWARD_RANK_RANGE_TITLE_BG_URL,
  PVP_TOURNAMENT_REWARD_RANK_SMALL_TITLE_BG_URL,
  PVP_TOURNAMENT_MY_RANK_NUMBER_BG_URL,
] as const

/**
 * Tournament hub art URLs — spread at the top of `VERCEL_BLOB_PRELOAD_PRIORITIZED` so `ensureVercelBlobPreload()`
 * warms them in early batches (join BG URL matches lobby BG asset).
 * Includes enter-battle modal title strips: leaderboard, activity (`activity-title-bg`), rewards (`reward-title-bg`).
 */
export const PVP_TOURNAMENT_PRELOAD_URLS: readonly string[] = [
  pvpTournamentLandButtonUrl,
  pvpTournamentJoinButtonUrl,
  pvpTournamentEnterBattleButtonUrl,
  pvpTournamentOneTimeRewardPanelBgUrl,
  pvpTournamentOneTimeClaimButtonBgUrl,
  PVP_TOURNAMENT_JOIN_PAGE_BG_URL,
  PVP_TOURNAMENT_JOIN_PAGE_TITLE_URL,
  PVP_TOURNAMENT_TOTAL_JOINERS_BG_URL,
  PVP_TOURNAMENT_HOW_IT_WORKS_URL,
  PVP_TOURNAMENT_SEASON_FLAG_URL,
  PVP_TOURNAMENT_TIME_PANEL_URL,
  PVP_TOURNAMENT_OVERVIEW_WEBP_URL,
  PVP_TOURNAMENT_WEEKLY_STATS_WEBP_URL,
  PVP_TOURNAMENT_LEADERBOARD_ROUND_BUTTON_BG_URL,
  PVP_TOURNAMENT_REWARDS_ROUND_BUTTON_BG_URL,
  PVP_TOURNAMENT_ACTIVITY_ROUND_BUTTON_BG_URL,
  PVP_TOURNAMENT_LEADERBOARD_MODAL_TITLE_BG_URL,
  PVP_TOURNAMENT_ACTIVITY_MODAL_TITLE_BG_URL,
  PVP_TOURNAMENT_REWARD_MODAL_TITLE_BG_URL,
  PVP_TOURNAMENT_RESULT_MODAL_TITLE_BG_URL,
  PVP_TOURNAMENT_RESULT_AVATAR_CONTAINER_URLS[0],
  PVP_TOURNAMENT_RESULT_AVATAR_CONTAINER_URLS[1],
  PVP_TOURNAMENT_RESULT_AVATAR_CONTAINER_URLS[2],
  PVP_TOURNAMENT_LEADERBOARD_MODAL_BODY_BG_URL,
  PVP_TOURNAMENT_MODAL_CLOSE_BUTTON_URL,
  // PVP_TOURNAMENT_RANK_BG_URL,
  // PVP_TOURNAMENT_BODY_BG_URL,
  PVP_TOURNAMENT_LOBBY_BANNER_URL,
  ...PVP_TOURNAMENT_RANK_REWARD_ICON_URLS,
  ...PVP_TOURNAMENT_REWARD_MODAL_BODY_PRELOAD_URLS,
]

const PVP_SHELL_ARENA_OPACITY = 0.5

type PvPShellBackgroundLayersProps = {
  layoutMode?: AppLayoutMode
}

export const PvPShellBackgroundLayers: React.FC<PvPShellBackgroundLayersProps> = ({
  layoutMode = 'desktop',
}) => {
  const isMobileLandscape = layoutMode === 'mobile-landscape'

  return (
    <>
      <div
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          backgroundImage: `url(${BATTLE_ARENA_URL})`,
          backgroundSize: isMobileLandscape ? '400px auto' : 'auto 80%',
          backgroundPosition: isMobileLandscape ? 'calc(50% + 11.5rem) 2rem' : 'center 2.5rem',
          backgroundRepeat: 'no-repeat',
          opacity: PVP_SHELL_ARENA_OPACITY,
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none fixed inset-0 z-[1]"
        style={{
          backgroundImage: `url(${pvpJoinCardFadeOverlayUrl})`,
          backgroundSize: '100% 100%',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
        aria-hidden
      />
    </>
  )
}

/** Join-room mode tabs (lobby ranked / unranked); Vercel blob `pvp/`. */
export const pvpUnrankedTabButtonUrl = `${PVP_MODE_BADGE_DIR}/unranked-button.webp`
export const pvpRankedTabButtonUrl = `${PVP_MODE_BADGE_DIR}/ranked-button.webp`

/** Ranked stake currency toggles (Vercel blob `pvp/`). */
export const pvpStakeUsdtBgUrl = `${PVP_MODE_BADGE_DIR}/USDT.webp`
export const pvpStakeEcsBgUrl = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/eclipsera-gem.webp'

/**
 * Lobby / VS room / waiting header — replaces the “Player VS Player” text title.
 * Asset: `public/assets/images/land/PvP/pvp-title.webp`
 */
export const pvpPageTitleUrl = `${PVP_MODE_BADGE_DIR}/pvp-title.webp`

/** Right-rail PvP lobby buttons (Vercel blob `pvp/`). */
export const pvpLobbyLeaderboardBtnIconUrl = `${PVP_MODE_BADGE_DIR}/leaderboard-button.webp`
export const pvpLobbyBattleLogBtnIconUrl = `${PVP_MODE_BADGE_DIR}/my-battle-log-button.webp`
export const pvpLobbyRewardBtnIconUrl = `${PVP_MODE_BADGE_DIR}/reward-button.webp`

/** Honor rewards modal shells (Vercel blob `pvp/`). */
export const pvpHonorModalBgUrl = `${PVP_MODE_BADGE_DIR}/pvp-honor-bg.webp`
export const pvpHonorRewardPanelBgUrl = `${PVP_MODE_BADGE_DIR}/pvp-honor-reward-bg.webp`

/** Honor modal full-bleed wallpaper (`LandPageModal`). Use `none` so art is not darkened. */
export const pvpHonorWallpaperImageFilter = 'none'

/** Reward detail panel art (`HonorRewardsTierDetailPanel` background layer). */
export const pvpHonorRewardPanelImageFilter = 'none'

/** In-battle ATK / DEF row icons (Vercel blob `pvp/`). */
export const pvpBattleAttackIconUrl = `${PVP_MODE_BADGE_DIR}/pvp-battle-attack.webp`
export const pvpBattleDefenceIconUrl = `${PVP_MODE_BADGE_DIR}/pvp-battle-defence.webp`

/** PvP character selection grid / army slot frame (`public/.../PvP/characters/character-card-bg.webp`). */
export const pvpCharacterSelectCardBgUrl = `${PVP_MODE_BADGE_DIR}/characters/character-card-bg.webp`

const PVP_CHARACTERS_ART_DIR = `${PVP_MODE_BADGE_DIR}/characters`
const CHARACTER_FRONT_VIEW_BLOB_BASE =
  'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/characters-front-view-new'

/**
 * PvP selection card portrait: `public/.../PvP/characters/{id}-{rarity}.webp` (all lowercase)
 * Examples: `sakura-common.webp`, `sakura-rare.webp`, `sakura-legendary.webp`
 */
export function pvpCharacterPortraitUrl(characterId?: string, rarity?: string): string {
  const id = (characterId || 'sakura').toLowerCase()
  const raw = (rarity || 'common').toLowerCase()
  const r = raw === 'rare' || raw === 'legendary' ? raw : 'common'
  return `${PVP_CHARACTERS_ART_DIR}/${id}-${r}.webp`
}

/** Used when a local portrait file is missing. */
export function pvpCharacterPortraitBlobFallbackUrl(characterId?: string, rarity?: string): string {
  const id = (characterId || 'sakura').toLowerCase()
  const raw = (rarity || 'common').toLowerCase()
  const r = raw === 'rare' || raw === 'legendary' ? raw : 'common'
  return `${CHARACTER_FRONT_VIEW_BLOB_BASE}/${id}-${r}.webp`
}

/** Join-card artwork: same two-slice layout as 2v2 — `{1v1|2v2|3v3}/top{n}.webp` + `bottom{n}.webp`. */
export function pvpBattleCardSliceUrl(
  characterCount: 1 | 2 | 3,
  pair: number,
  slice: 'upper' | 'lower',
): string {
  const raw = Math.max(1, Math.floor(pair))
  /** Blobs only ship `top1`…`top5` / `bottom1`…`bottom5`; extra ranked ECS tiers reuse art cyclically (6→1, 7→2, …). */
  const n = raw > 5 ? raw % 5 || 5 : raw
  const dir =
    characterCount === 1
      ? PVP_1CHAR_BATTLE_CARD_DIR
      : characterCount === 2
        ? PVP_2CHAR_BATTLE_CARD_DIR
        : PVP_3CHAR_BATTLE_CARD_DIR
  return slice === 'upper' ? `${dir}/top${n}.webp` : `${dir}/bottom${n}.webp`
}

/** @deprecated Prefer `pvpBattleCardSliceUrl(2, pair, slice)`. */
export function pvpTwoCharBattleCardBgUrl(pair: number, slice: 'upper' | 'lower'): string {
  return pvpBattleCardSliceUrl(2, pair, slice)
}

type PvPBackgroundProps = {
  /** Rapid gold / orange / white strobe behind the main PvP UI (match found urgency). */
  urgentMatchStrobe?: boolean
}

export const PvPBackground: React.FC<PvPBackgroundProps> = ({
  urgentMatchStrobe = false,
}) => {
  if (!urgentMatchStrobe) return null

  return (
    <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
      {urgentMatchStrobe ? (
        <>
          <style>{`
            @keyframes pvp-bg-match-strobe-a {
              0%, 100% { opacity: 0; }
              12% { opacity: 0.95; }
              24% { opacity: 0.08; }
              38% { opacity: 0.88; }
              51% { opacity: 0.05; }
              64% { opacity: 0.92; }
              78% { opacity: 0.12; }
            }
            @keyframes pvp-bg-match-strobe-b {
              0%, 100% { opacity: 0; }
              18% { opacity: 0.55; }
              33% { opacity: 0.1; }
              47% { opacity: 0.8; }
              61% { opacity: 0.15; }
              74% { opacity: 0.7; }
            }
            @keyframes pvp-bg-match-strobe-c {
              0%, 100% { opacity: 0; }
              8% { opacity: 0.75; }
              22% { opacity: 0.2; }
              44% { opacity: 0.9; }
              58% { opacity: 0.08; }
              71% { opacity: 0.65; }
              86% { opacity: 0.18; }
            }
          `}</style>
          <div
            className="absolute inset-0 z-[1] mix-blend-screen"
            style={{
              background:
                'radial-gradient(ellipse 130% 90% at 50% 15%, rgba(255,252,240,0.9) 0%, rgba(255,200,80,0.45) 28%, rgba(251,146,60,0.25) 48%, transparent 72%)',
              animation: 'pvp-bg-match-strobe-a 0.24s steps(1, end) infinite',
            }}
          />
          <div
            className="absolute inset-0 z-[2] mix-blend-overlay"
            style={{
              background:
                'linear-gradient(118deg, transparent 0%, rgba(255,255,255,0.55) 42%, rgba(254,215,170,0.5) 50%, rgba(251,146,60,0.4) 58%, transparent 100%)',
              animation: 'pvp-bg-match-strobe-b 0.32s steps(1, end) infinite',
            }}
          />
          <div
            className="absolute inset-0 z-[3]"
            style={{
              mixBlendMode: 'soft-light',
              background:
                'linear-gradient(180deg, rgba(253,230,138,0.55) 0%, transparent 35%, transparent 65%, rgba(255,255,255,0.35) 100%)',
              animation: 'pvp-bg-match-strobe-c 0.27s steps(1, end) infinite',
            }}
          />
        </>
      ) : null}
    </div>
  )
}

export { BACKGROUND_URL, BATTLE_BACKGROUND_URL, BATTLE_ARENA_URL, PVP_BATTLE_CARD_TILE_BG_URL }
